// Iter 31.270 — Generic micro-CMS public renderer.
//
// One component, parametric on `siteSpec`. Powers the 4 new .uk domains
// (spyhealth, spyradio, spysports, spycareer) — Gary's Shed keeps its
// own page module so its content + bespoke styling don't move.
//
// Each public domain's CF Worker reverse-proxies `<domain>/*` →
// `spyfinance.cloud/{spa_prefix}/*`, so visitors see this UI under the
// masked hostname. Two manages content via the `update_site_page` MCP
// tool — no UI form here.
//
// Public, no auth.

import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 31.278 — `rehypeRaw` lets raw HTML embedded in the markdown body
// (e.g. `<img src="..." style="max-height:400px" />`) render as actual
// HTML instead of escaped text. Without it react-markdown drops inline
// HTML on the floor. Two files site content via MCP — any richer-than-
// markdown formatting (image size caps, embed blocks) rides through as
// raw HTML nodes, so this plugin is mandatory for both index + page
// renderers below.
const _REHYPE_PLUGINS = [rehypeRaw];

// Per Two's brief — six sites. The `domain` field doubles as the
// host-mode lookup key in App.js.
export const SITE_SPECS = {
    spyhealth: {
        siteId: "spyhealth",
        label: "Spy Health",
        tagline: "A field for the body.",
        domain: "spyhealth.uk",
        spaPrefix: "/spyhealth",
    },
    spyradio: {
        siteId: "spyradio",
        label: "Spy Radio",
        tagline: "Songs from the station.",
        domain: "spyradio.uk",
        spaPrefix: "/spyradio",
    },
    spysports: {
        siteId: "spysports",
        label: "Spy Sports",
        tagline: "Footballers welcome.",
        domain: "spysports.uk",
        spaPrefix: "/spysports",
    },
    spycareer: {
        siteId: "spycareer",
        label: "Spy Career",
        tagline: "The graph of the work.",
        domain: "spycareer.uk",
        spaPrefix: "/spycareer",
    },
    // Iter 31.287 — Two's brief (09 Jul 2026): deliberate visual
    // break from the Village aesthetic. Own page module + theme —
    // see /app/frontend/src/pages/SpyAcademy.jsx.
    spyacademy: {
        siteId: "spyacademy",
        label: "Spy Academy",
        tagline: "The quiet study of craft.",
        domain: "spyacademy.uk",
        spaPrefix: "/spyacademy",
    },
};

// Host-aware route prefix. On the masked domain the SPA is mounted at
// "/", so internal markdown links like `/about` resolve directly.
// On spyfinance.cloud the same SPA is mounted at `${siteSpec.spaPrefix}`,
// so the same link needs the prefix.
function _routePrefix(siteSpec) {
    if (typeof window === "undefined") return siteSpec.spaPrefix;
    return window.location.hostname === siteSpec.domain ? "" : siteSpec.spaPrefix;
}

// Markdown link renderer — same host-aware rewriting as GarysShed.
function _makeMarkdownLink(siteSpec) {
    return function MarkdownLink({ href, children, ...rest }) {
        if (!href) return <span {...rest}>{children}</span>;
        const isInternal = href.startsWith("/") && !href.startsWith("//");
        const isAnchor = href.startsWith("#");
        if (isAnchor) return <a href={href} {...rest}>{children}</a>;
        if (isInternal) {
            const prefix = _routePrefix(siteSpec);
            const path = href.startsWith(prefix + "/") || href === prefix
                ? href
                : prefix + href;
            return (
                <Link
                    to={path}
                    className="text-ink underline decoration-stamp-red decoration-1 underline-offset-2 hover:text-stamp-red transition-colors"
                    {...rest}
                >
                    {children}
                </Link>
            );
        }
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink underline decoration-stamp-red decoration-1 underline-offset-2 hover:text-stamp-red transition-colors"
                {...rest}
            >
                {children}
            </a>
        );
    };
}

function _setMeta(name, content, attr = "name") {
    if (typeof document === "undefined") return;
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function useDocumentMeta({ title, description }) {
    useEffect(() => {
        if (typeof document === "undefined") return;
        const prevTitle = document.title;
        if (title) document.title = title;
        if (description) {
            _setMeta("description", description);
            _setMeta("og:title", title || prevTitle, "property");
            _setMeta("og:description", description, "property");
            _setMeta("og:type", "article", "property");
            _setMeta("twitter:card", "summary");
            _setMeta("twitter:title", title || prevTitle);
            _setMeta("twitter:description", description);
        }
        return () => { document.title = prevTitle; };
    }, [title, description]);
}

export function SiteIndex({ siteSpec }) {
    const [pages, setPages] = useState(null);
    const [indexPage, setIndexPage] = useState(null);
    const [err, setErr] = useState("");
    // Iter 31.280 — parallel fetch + `ready` gate, mirror of the fix
    // Three landed on GarysShedIndex. Without it the origin's fallback
    // page list flashes for ~500ms during React hydration on the .uk
    // sibling domains before the CMS index body arrives.
    const [ready, setReady] = useState(false);
    const mdComponents = useMemo(() => ({ a: _makeMarkdownLink(siteSpec) }), [siteSpec]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [pagesRes, indexRes] = await Promise.allSettled([
                axios.get(`${API}/api/${siteSpec.siteId}/pages`),
                axios.get(`${API}/api/${siteSpec.siteId}/page/index`),
            ]);
            if (cancelled) return;
            if (pagesRes.status === "fulfilled") {
                setPages(pagesRes.value.data.pages || []);
            } else {
                setErr(`Couldn't load pages: ${pagesRes.reason?.message || "unknown"}`);
            }
            if (indexRes.status === "fulfilled") {
                setIndexPage(indexRes.value.data);
            }
            setReady(true);
        })();
        return () => { cancelled = true; };
    }, [siteSpec]);

    const fallbackList = useMemo(() => {
        if (indexPage) return [];
        return (pages || []).filter((p) => p.slug !== "index");
    }, [pages, indexPage]);

    const prefix = _routePrefix(siteSpec);

    useDocumentMeta({
        title: indexPage?.title
            ? `${indexPage.title} · ${siteSpec.label}`
            : siteSpec.label,
        description: indexPage?.og_description || siteSpec.tagline,
    });

    return (
        <div className="min-h-screen bg-paper text-ink public-site" data-testid={`site-${siteSpec.siteId}-index`}>
            <div
                className={`max-w-2xl mx-auto px-6 py-16 transition-opacity duration-[150ms] ${
                    ready ? "opacity-100" : "opacity-0"
                }`}
            >
                <h1
                    className="font-heading uppercase tracking-[0.22em] text-3xl mb-3"
                    data-testid={`site-${siteSpec.siteId}-title`}
                >
                    {siteSpec.label}
                </h1>
                <p className="font-body text-sm text-ink-soft mb-12 leading-relaxed">
                    {siteSpec.tagline}
                </p>

                {!ready ? (
                    <div
                        className="font-mono text-xs text-ink-soft opacity-0"
                        data-testid={`site-${siteSpec.siteId}-index-hydrating`}
                        aria-hidden="true"
                    >
                        &nbsp;
                    </div>
                ) : indexPage ? (
                    <div
                        className="prose prose-sm max-w-none font-body text-ink leading-relaxed"
                        data-testid={`site-${siteSpec.siteId}-index-body`}
                    >
                        <ReactMarkdown components={mdComponents} rehypePlugins={_REHYPE_PLUGINS}>
                            {indexPage.body_md}
                        </ReactMarkdown>
                    </div>
                ) : err ? (
                    <div className="font-mono text-xs text-stamp-red">{err}</div>
                ) : fallbackList.length === 0 ? (
                    <div className="font-body text-sm text-ink-soft" data-testid={`site-${siteSpec.siteId}-empty`}>
                        Nothing here yet.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid={`site-${siteSpec.siteId}-list`}>
                        {fallbackList.map((p) => (
                            <li key={`${p.slug}-${p.version}`}>
                                <Link
                                    to={`${prefix}/${p.slug}`}
                                    className="font-heading uppercase tracking-[0.18em] text-base text-ink hover:text-stamp-red transition-colors"
                                    data-testid={`site-${siteSpec.siteId}-link-${p.slug}`}
                                >
                                    {p.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export function SitePage({ siteSpec }) {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const mdComponents = useMemo(() => ({ a: _makeMarkdownLink(siteSpec) }), [siteSpec]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/api/${siteSpec.siteId}/page/${slug}`);
                if (!cancelled) setPage(data);
            } catch (e) {
                if (!cancelled) {
                    if (e.response?.status === 404) setErr("Not here.");
                    else setErr(`Couldn't load: ${e.message}`);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [siteSpec, slug]);

    const prefix = _routePrefix(siteSpec);

    useDocumentMeta({
        title: page?.title ? `${page.title} · ${siteSpec.label}` : siteSpec.label,
        description: page?.og_description || "",
    });

    return (
        <div className="min-h-screen bg-paper text-ink public-site" data-testid={`site-${siteSpec.siteId}-page-${slug}`}>
            <div className="max-w-2xl mx-auto px-6 py-16">
                <div className="mb-8">
                    <Link
                        to={prefix || "/"}
                        className="font-mono text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-stamp-red transition-colors"
                        data-testid={`site-${siteSpec.siteId}-back-link`}
                    >
                        ← back
                    </Link>
                </div>
                {loading ? (
                    <div className="font-mono text-xs text-ink-soft">loading…</div>
                ) : err ? (
                    <div data-testid={`site-${siteSpec.siteId}-page-error`}>
                        <h1 className="font-heading uppercase tracking-[0.22em] text-2xl mb-3">{err}</h1>
                    </div>
                ) : page ? (
                    <article>
                        <h1
                            className="font-heading uppercase tracking-[0.22em] text-3xl mb-8"
                            data-testid={`site-${siteSpec.siteId}-page-title`}
                        >
                            {page.title}
                        </h1>
                        <div
                            className="prose prose-sm max-w-none font-body text-ink leading-relaxed"
                            data-testid={`site-${siteSpec.siteId}-page-body`}
                        >
                            <ReactMarkdown components={mdComponents} rehypePlugins={_REHYPE_PLUGINS}>
                                {page.body_md}
                            </ReactMarkdown>
                        </div>
                    </article>
                ) : null}
            </div>
        </div>
    );
}
